import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, Dumbbell, Ruler, TrendingUp, Plus, Printer, Trash2, Info, Sparkles, CalendarCheck, MessageSquareText, Send, History, ChevronUp } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from 'recharts';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import TrainingPlanView from './TrainingPlanView';
import { Textarea } from './ui/textarea';
import { portalTraining, portalAddMeasurement, portalAddWorkoutLog, portalCoachMessages, portalCoachMessageSend } from '../services/api';

const fmtD = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('hu-HU', { month: '2-digit', day: '2-digit' }); } catch { return ''; }
};
const fmtFull = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('hu-HU'); } catch { return iso; }
};

/**
 * Per-exercise 1RM trend chart. Renders a small selector for the athlete's
 * top exercises (>=2 logs) and a Recharts LineChart of the estimated 1RM
 * over time. Empty state hidden; the panel simply doesn't render if there
 * aren't at least 2 comparable logs for any exercise.
 */
export const OneRmTrendChart = ({ workoutLogs, theme = 'light' }) => {
  const isDark = theme === 'dark';
  // Bucket logs by exercise and keep only those with 2+ data points so a
  // trend actually exists to visualize.
  const perExercise = React.useMemo(() => {
    const map = {};
    (workoutLogs || []).forEach((l) => {
      const est = oneRmEpley(l.weight_kg, l.reps);
      if (est == null || !l.exercise_name) return;
      const key = l.exercise_name.trim();
      if (!map[key]) map[key] = [];
      map[key].push({
        date: new Date(l.created_at).toISOString().slice(0, 10),
        oneRm: Math.round(est * 10) / 10,
        weight: parseFloat(l.weight_kg),
        reps: parseInt(l.reps, 10),
        ts: new Date(l.created_at).getTime(),
      });
    });
    Object.values(map).forEach((arr) => arr.sort((a, b) => a.ts - b.ts));
    return Object.fromEntries(Object.entries(map).filter(([, v]) => v.length >= 2));
  }, [workoutLogs]);

  const exerciseNames = Object.keys(perExercise);
  const [selected, setSelected] = React.useState(exerciseNames[0] || '');

  React.useEffect(() => {
    // If the currently-selected exercise disappears (e.g. logs deleted),
    // fall back to the first available one.
    if (exerciseNames.length && !perExercise[selected]) setSelected(exerciseNames[0]);
  }, [exerciseNames, perExercise, selected]);

  if (!exerciseNames.length) return null;

  const rows = perExercise[selected] || [];
  const first = rows[0]?.oneRm;
  const last = rows[rows.length - 1]?.oneRm;
  const deltaKg = first != null && last != null ? Math.round((last - first) * 10) / 10 : null;
  const deltaPct = first ? Math.round(((last - first) / first) * 1000) / 10 : null;

  return (
    <div
      data-testid="onerm-trend-chart"
      className={`mt-5 pt-4 border-t ${isDark ? 'border-gray-800' : 'border-gray-100'}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className={`text-[10px] uppercase tracking-widest ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>1RM trend — gyakorlatonként</div>
        <select
          data-testid="onerm-trend-exercise-select"
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          className={`text-xs font-semibold rounded-md px-2 py-1.5 focus:outline-none focus:border-yellow-500 ${isDark ? 'bg-gray-800 border border-gray-700 text-gray-100' : 'bg-white border border-gray-300 text-gray-800'}`}
        >
          {exerciseNames.map((n) => (
            <option key={n} value={n}>{n} ({perExercise[n].length} bejegyzés)</option>
          ))}
        </select>
      </div>

      {deltaKg != null && (
        <div className={`mb-2 text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          Első becsült 1RM: <span className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{first} kg</span> →
          {' '}legutóbbi: <span className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{last} kg</span>
          {deltaKg !== 0 && (
            <span
              data-testid="onerm-trend-delta"
              className={`ml-2 font-bold ${deltaKg > 0 ? (isDark ? 'text-emerald-400' : 'text-emerald-700') : (isDark ? 'text-red-400' : 'text-red-600')}`}
            >
              {deltaKg > 0 ? '▲' : '▼'} {Math.abs(deltaKg)} kg
              {deltaPct != null && ` (${deltaPct > 0 ? '+' : ''}${deltaPct}%)`}
            </span>
          )}
        </div>
      )}

      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={rows} margin={{ top: 5, right: 10, bottom: 0, left: -15 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#e5e7eb'} />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 10, fill: isDark ? '#9ca3af' : '#6b7280' }}
              padding={{ left: 18, right: 18 }}
            />
            <YAxis tick={{ fontSize: 10, fill: isDark ? '#9ca3af' : '#6b7280' }} domain={['dataMin - 5', 'dataMax + 5']} />
            <Tooltip
              contentStyle={{ background: '#111827', border: 'none', borderRadius: 8 }}
              labelStyle={{ color: '#facc15', fontWeight: 700 }}
              itemStyle={{ color: '#f9fafb' }}
              formatter={(value, name, props) => {
                if (name === 'oneRm') {
                  const p = props.payload || {};
                  return [`${value} kg (${p.weight}×${p.reps})`, 'Becsült 1RM'];
                }
                return [value, name];
              }}
            />
            <Line
              type="monotone"
              dataKey="oneRm"
              stroke="#ca8a04"
              strokeWidth={2.5}
              dot={{ r: 4, fill: '#ca8a04' }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className={`mt-1 text-[10px] italic ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
        Epley-formula alapján számolva: 1RM = súly × (1 + ismétlés / 30).
      </div>
    </div>
  );
};

/**
 * Epley 1RM formula — the industry standard for estimating a one-rep max
 * from a submaximal lift: 1RM = w × (1 + reps/30). Accurate up to ~10 reps.
 * Returns null when input is incomplete.
 */
export const oneRmEpley = (weight, reps) => {
  const w = parseFloat(weight);
  const r = parseInt(reps, 10);
  if (!w || !r || r < 1 || r > 20) return null;
  return w * (1 + r / 30);
};

/**
 * Map current-set %1RM → training zone label + rough RPE hint. These are
 * the widely-used strength-coaching bands (Prilepin / Rippetoe adaptation);
 * intentionally coarse so the coach can override in-app comments.
 */
export const percentZone = (pct) => {
  if (pct < 60)  return { label: 'Bemelegítő zóna',    rpe: '~4-5',  color: 'bg-slate-500',  ok: false };
  if (pct < 70)  return { label: 'Erő-állóképesség',   rpe: '~5-6',  color: 'bg-amber-500',  ok: false };
  if (pct < 85)  return { label: 'Izomépítő zóna',     rpe: '~7-8',  color: 'bg-emerald-500', ok: true  };
  if (pct <= 100) return { label: 'Maximális erő',     rpe: '~9-10', color: 'bg-red-500',    ok: true  };
  return { label: 'Túl a becsült 1RM-en',              rpe: '~10+',  color: 'bg-red-600',    ok: true  };
};

/**
 * Mini visual bar covering 50–100% of 1RM. A green marker shows the current
 * position; a subtle vertical tick at 70% signals the hypertrophy threshold
 * requested by the coach. Position is clamped to the visible 50–100 window,
 * i.e. a <50% warm-up shows the marker at the far left.
 */
export const OneRmBar = ({ percent }) => {
  const clamped = Math.max(50, Math.min(100, percent));
  // Map [50..100] → [0..100] of the bar width so 70% sits at 40% along.
  const barPos = ((clamped - 50) / 50) * 100;
  const seventyPos = ((70 - 50) / 50) * 100;
  const zone = percentZone(percent);
  return (
    <div className="mt-1.5" data-testid="onerm-bar-wrap">
      <div className="relative h-2 rounded-full bg-gradient-to-r from-slate-200 via-amber-100 to-red-100 overflow-visible">
        {/* Hypertrophy threshold tick */}
        <div
          className="absolute top-[-3px] bottom-[-3px] w-[1.5px] bg-emerald-700/80"
          style={{ left: `${seventyPos}%` }}
          aria-hidden="true"
        />
        {/* Current-lift marker */}
        <div
          data-testid="onerm-bar-marker"
          className={`absolute -top-1 h-4 w-4 rounded-full ${zone.color} border-2 border-white shadow`}
          style={{ left: `calc(${barPos}% - 8px)` }}
          title={`${Math.round(percent)}% az 1RM-ből`}
        />
      </div>
      <div className="mt-1 flex justify-between text-[10px] text-gray-400 font-semibold">
        <span>50%</span>
        <span className="text-emerald-700">70% · izomépítés</span>
        <span>100%</span>
      </div>
    </div>
  );
};

/**
 * Body-measurement form. Gender-aware: shows the fields needed for the
 * relevant golden-ratio index and hides those that would be dead weight.
 *
 *   - Male   → Testsúly · Derék · Vállkörméret   (Adonis Index = shoulder/waist, ideal 1.618)
 *   - Female → Testsúly · Derék · Csípő          (Venus Index  = waist/hip,     ideal 0.7)
 *   - Unknown gender → falls back to the previous 4-field layout so pre-2026-09
 *     accounts that never filled the calculator still see something usable.
 */
const MeasurementForm = ({ gender, onSaved }) => {
  const [form, setForm] = useState({ weight_kg: '', waist_cm: '', neck_cm: '', hip_cm: '', shoulder_cm: '' });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    const payload = {};
    Object.entries(form).forEach(([k, v]) => { if (v !== '') payload[k] = parseFloat(v); });
    if (Object.keys(payload).length === 0) { toast.error('Adj meg legalább egy értéket.'); return; }
    setSaving(true);
    try {
      await portalAddMeasurement(payload);
      toast.success('Mérés elmentve.');
      setForm({ weight_kg: '', waist_cm: '', neck_cm: '', hip_cm: '', shoulder_cm: '' });
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'A mentés nem sikerült.');
    } finally { setSaving(false); }
  };

  const fields = gender === 'male'
    ? [['weight_kg', 'Testsúly (kg)'], ['waist_cm', 'Derék (cm)'], ['shoulder_cm', 'Vállkörméret (cm)']]
    : gender === 'female'
    ? [['weight_kg', 'Testsúly (kg)'], ['waist_cm', 'Derék (cm)'], ['hip_cm', 'Csípő (cm)']]
    : [['weight_kg', 'Testsúly (kg)'], ['waist_cm', 'Derék (cm)'], ['neck_cm', 'Nyak (cm)'], ['hip_cm', 'Csípő (cm)']];
  return (
    <div className={`grid grid-cols-2 gap-3 items-end ${fields.length === 3 ? 'md:grid-cols-4' : 'md:grid-cols-5'}`}>
      {fields.map(([k, label]) => (
        <div key={k}>
          <Label className="text-[10px] uppercase tracking-widest text-gray-500">{label}</Label>
          <Input data-testid={`measure-${k}`} type="number" step="0.1" value={form[k]} onChange={set(k)} className="mt-1 h-10" />
        </div>
      ))}
      <Button data-testid="measure-save-btn" onClick={submit} disabled={saving} className="bg-yellow-600 hover:bg-yellow-700 text-white font-bold h-10">
        {saving ? <Loader2 className="animate-spin" size={14} /> : <><Plus size={14} className="mr-1" /> Rögzítés</>}
      </Button>
    </div>
  );
};

const EMPTY_SET = { kg: '', reps: '', rpe: '' };

const WorkoutLogForm = ({ plan, workoutLogs, onSaved }) => {
  const exercises = (plan?.exercises || []).filter((e) => (e.name || '').trim());
  const [exerciseId, setExerciseId] = useState('');
  const [sets, setSets] = useState([{ ...EMPTY_SET }, { ...EMPTY_SET }, { ...EMPTY_SET }]);
  const [saving, setSaving] = useState(false);
  const [showRirInfo, setShowRirInfo] = useState(false);
  const [showLastLog, setShowLastLog] = useState(false);

  const setVal = (i, k) => (e) => setSets((rows) => rows.map((r, ri) => (ri === i ? { ...r, [k]: e.target.value } : r)));
  const addRow = () => setSets((r) => [...r, { ...EMPTY_SET }]);
  const delRow = (i) => setSets((r) => (r.length > 1 ? r.filter((_, ri) => ri !== i) : r));

  // Find the most recent workout log for the currently selected exercise
  // (case-insensitive name match). Prefer newest by created_at, fall back to
  // list order for legacy rows without timestamps.
  const selectedExercise = useMemo(
    () => exercises.find((e) => e.id === exerciseId) || null,
    [exercises, exerciseId]
  );
  const lastLogForExercise = useMemo(() => {
    if (!selectedExercise) return null;
    const key = (selectedExercise.name || '').trim().toLowerCase();
    if (!key) return null;
    const candidates = (workoutLogs || []).filter(
      (l) => (l.exercise_name || '').trim().toLowerCase() === key
    );
    if (!candidates.length) return null;
    return candidates
      .slice()
      .sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime())[0];
  }, [workoutLogs, selectedExercise]);

  // Auto-hide the panel when the exercise selection changes so the user
  // has to opt in explicitly for the new exercise's history.
  useEffect(() => { setShowLastLog(false); }, [exerciseId]);

  const submit = async () => {
    const ex = exercises.find((e) => e.id === exerciseId);
    if (!ex) { toast.error('Válassz gyakorlatot.'); return; }
    const filled = sets.filter((s) => s.kg !== '' || s.reps !== '' || s.rpe !== '');
    if (filled.length === 0) { toast.error('Adj meg legalább egy szettet.'); return; }
    const incomplete = filled.some((s) => s.kg === '' || s.reps === '' || s.rpe === '');
    if (incomplete) { toast.error('Minden szettnél add meg a súlyt, az ismétlést ÉS az RPE-t.'); return; }
    setSaving(true);
    try {
      await portalAddWorkoutLog({
        plan_id: plan.id,
        exercise_id: ex.id,
        exercise_name: ex.name,
        sets: filled.map((s) => ({
          kg: parseFloat(s.kg), reps: parseInt(s.reps, 10),
          rir: Math.max(0, Math.min(10, 10 - parseInt(s.rpe, 10))),
        })),
      });
      toast.success('Edzésnapló bejegyzés elmentve.');
      setSets([{ ...EMPTY_SET }, { ...EMPTY_SET }, { ...EMPTY_SET }]);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'A mentés nem sikerült.');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-3">
      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-gray-500">Gyakorlat</Label>
          <select
            data-testid="log-exercise-select"
            value={exerciseId}
            onChange={(e) => setExerciseId(e.target.value)}
            className="mt-1 h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm"
          >
            <option value="">Válassz…</option>
            {exercises.map((e) => <option key={e.id} value={e.id}>{e.muscle_group ? `${e.muscle_group} — ` : ''}{e.name}</option>)}
          </select>
        </div>
        <div className="flex items-end gap-3">
          {selectedExercise && (
            <button
              type="button"
              data-testid="last-log-toggle"
              onClick={() => setShowLastLog((v) => !v)}
              className={`inline-flex items-center gap-1.5 text-xs font-bold pb-2.5 transition-colors ${
                showLastLog ? 'text-emerald-800' : 'text-emerald-700 hover:text-emerald-900'
              }`}
              title="Utolsó edzésnaplód a kiválasztott gyakorlatra"
            >
              {showLastLog ? <ChevronUp size={13} /> : <History size={13} />} Legutóbbi edzés
            </button>
          )}
          <button
            type="button"
            data-testid="rir-info-toggle"
            onClick={() => setShowRirInfo((v) => !v)}
            className="inline-flex items-center gap-1.5 text-xs text-sky-700 hover:text-sky-900 font-semibold pb-2.5"
          >
            <Info size={13} /> Mi az az RPE?
          </button>
        </div>
      </div>
      {selectedExercise && showLastLog && (
        <div
          data-testid="last-log-panel"
          className="rounded-xl border-2 border-emerald-400 bg-gradient-to-br from-emerald-50 to-white ring-1 ring-emerald-200 shadow-sm px-4 py-3"
        >
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-500 flex items-center justify-center shrink-0">
              <History size={16} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5">
                <span className="text-[10px] font-black uppercase tracking-widest text-emerald-700">Legutóbbi edzés</span>
                <span className="text-xs text-emerald-800 font-semibold">
                  {selectedExercise.name}
                </span>
                {lastLogForExercise ? (
                  <span className="text-[11px] text-emerald-700/80">
                    · {fmtFull(lastLogForExercise.created_at)}
                  </span>
                ) : null}
              </div>
              {lastLogForExercise ? (
                <>
                  {(() => {
                    // Support both schemas: (a) modern `sets: [{kg, reps, rir}]`,
                    // (b) legacy top-level `weight_kg / reps / sets_done`.
                    const modernSets = Array.isArray(lastLogForExercise.sets) ? lastLogForExercise.sets : [];
                    const derivedSets = modernSets.length
                      ? modernSets
                      : (lastLogForExercise.weight_kg && lastLogForExercise.reps
                          ? Array.from({ length: Math.max(1, lastLogForExercise.sets_done || 1) }, () => ({
                              kg: lastLogForExercise.weight_kg,
                              reps: lastLogForExercise.reps,
                            }))
                          : []);
                    if (!derivedSets.length) return null;
                    // Highest 1RM estimate across the sets.
                    const topSet = derivedSets.reduce((best, s) => {
                      const est = oneRmEpley(s.kg, s.reps);
                      if (est == null) return best;
                      if (!best || est > best.est) return { est, s };
                      return best;
                    }, null);
                    return (
                      <>
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {derivedSets.map((s, si) => (
                            <span
                              key={si}
                              data-testid={`last-log-set-${si}`}
                              className="inline-flex items-center gap-1 text-xs font-bold bg-white border-2 border-emerald-500 text-emerald-800 px-2.5 py-1 rounded-md shadow-sm"
                            >
                              <span className="text-[9px] font-black uppercase tracking-wider text-emerald-500">{si + 1}.</span>
                              {s.kg} kg × {s.reps}
                              {typeof s.rir === 'number' && (
                                <span className="text-emerald-500 font-normal">· RPE {Math.max(1, Math.min(10, 10 - s.rir))}</span>
                              )}
                            </span>
                          ))}
                        </div>
                        {topSet && (
                          <div
                            data-testid="last-log-topset"
                            className="mt-2 text-[11px] text-emerald-800/90 leading-snug"
                          >
                            <span className="font-bold">Legjobb szett:</span>{' '}
                            {topSet.s.kg} kg × {topSet.s.reps} → becsült 1RM ~{topSet.est.toFixed(1)} kg.{' '}
                            <span className="text-emerald-700/70">Cél: szárnyald túl!</span>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </>
              ) : (
                <div className="text-xs text-emerald-800/70 mt-1">Ehhez a gyakorlathoz még nincs korábbi bejegyzésed — most alapozod meg a történetet. 💪</div>
              )}
            </div>
          </div>
        </div>
      )}
      {showRirInfo && (
        <div data-testid="rir-info-box" className="rounded-lg bg-sky-50 border border-sky-200 px-4 py-3 text-xs text-sky-900 space-y-2">
          <p>
            <strong>RPE = mennyire volt nehéz a szett, 1–10-ig.</strong><br />
            10 = teljes bukásig mentél, több ismétlés nem ment volna · 9 = még 1 ismétlés belefért volna ·
            8 = még 2 ismétlés · 7 = még 3 ismétlés.
          </p>
          <p>
            <strong>Miért nem használjuk az 1–6-os tartományt izomépítésre?</strong><br />
            RPE 1–6 azt jelenti, hogy még 4 vagy több ismétlés maradt volna benned — ilyenkor a szett túl könnyű
            ahhoz, hogy növekedésre késztesse az izmot. Ez a tartomány bemelegítésre, technika-gyakorlásra és
            aktív pihenőre való. Izomépítéshez a munkaszetteknek jellemzően <strong>RPE 7–9</strong> között kell
            lenniük: elég nehéz a fejlődéshez, de marad tartalék a tiszta technikához és a regenerációhoz.
          </p>
          <p>Ebből tudjuk pontosan követni a fejlődésed — becsüld meg őszintén!</p>
        </div>
      )}
      <div className="space-y-2">
        {sets.map((s, i) => (
          <div key={i} className="grid grid-cols-[auto_1fr_1fr_1fr_auto] gap-2 items-center">
            <span className="text-[10px] uppercase tracking-widest text-gray-400 w-12">{i + 1}. szett</span>
            <Input data-testid={`log-set-${i}-kg`} type="number" step="0.5" min="0" placeholder="Súly (kg)" value={s.kg} onChange={setVal(i, 'kg')} className="h-10" />
            <Input data-testid={`log-set-${i}-reps`} type="number" step="1" min="0" placeholder="Ismétlés" value={s.reps} onChange={setVal(i, 'reps')} className="h-10" />
            <Input data-testid={`log-set-${i}-rpe`} type="number" step="1" min="1" max="10" placeholder="RPE (1-10)" value={s.rpe} onChange={setVal(i, 'rpe')} className="h-10" />
            <button type="button" data-testid={`log-set-${i}-del`} onClick={() => delRow(i)} className="p-2 text-gray-400 hover:text-red-500" title="Szett törlése">
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button data-testid="log-add-set-btn" type="button" variant="outline" size="sm" onClick={addRow} className="border-gray-300 text-gray-700 hover:bg-gray-100">
          <Plus size={13} className="mr-1" /> Szett hozzáadása
        </Button>
        <Button data-testid="log-save-btn" onClick={submit} disabled={saving} size="sm" className="bg-gray-900 hover:bg-yellow-600 text-white font-bold">
          {saving ? <Loader2 className="animate-spin" size={14} /> : <><Plus size={14} className="mr-1" /> Beírom</>}
        </Button>
      </div>
    </div>
  );
};

// Neutral verdict chips — never red (a visszaesés nem kudarc)
const VERDICT_CHIP = {
  'fejlődés':   { label: 'Fejlődés',    cls: 'bg-emerald-100 text-emerald-800' },
  'plató':      { label: 'Stabil szint', cls: 'bg-amber-100 text-amber-800' },
  'visszaesés': { label: 'Ingadozás',   cls: 'bg-sky-100 text-sky-800' },
  'gyűjtés':    { label: 'Adatgyűjtés', cls: 'bg-gray-100 text-gray-600' },
  'fogyás':     { label: 'Fogyás',      cls: 'bg-emerald-100 text-emerald-800' },
  'hízás':      { label: 'Súlynövekedés', cls: 'bg-amber-100 text-amber-800' },
  'csökkenés':  { label: 'Csökkenés',   cls: 'bg-emerald-100 text-emerald-800' },
  'növekedés':  { label: 'Növekedés',   cls: 'bg-amber-100 text-amber-800' },
};

const VerdictChip = ({ verdict, testId }) => {
  const c = VERDICT_CHIP[verdict] || VERDICT_CHIP['gyűjtés'];
  return (
    <span data-testid={testId} className={`inline-flex items-center text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded ${c.cls}`}>
      {c.label}
    </span>
  );
};

const LEVEL_HU = { 'kezdő': 'Kezdő', 'középhaladó': 'Középhaladó', 'haladó': 'Haladó' };

const ProgressFeedback = ({ feedback }) => {
  if (!feedback) return null;
  const fb = feedback;
  const strengthWithData = (fb.strength || []).filter((s) => s.series.length > 0);
  const showE1rm = fb.level !== 'kezdő';

  return (
    <div data-testid="portal-progress-feedback" className="rounded-2xl border border-gray-200 bg-white p-5 md:p-7">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="text-xs font-bold uppercase tracking-widest text-gray-500 flex items-center gap-2">
          <Sparkles size={14} className="text-yellow-700" /> Haladásod — visszajelzés
        </h3>
        <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded bg-gray-900 text-yellow-400">
          {LEVEL_HU[fb.level] || fb.level} szint
        </span>
      </div>

      <div data-testid="feedback-headline" className={`rounded-xl px-4 py-3.5 text-sm leading-relaxed mb-5 ${
        fb.recomposition ? 'bg-emerald-50 border border-emerald-200 text-emerald-900' : 'bg-gray-50 border border-gray-200 text-gray-800'
      }`}>
        {fb.headline}
      </div>

      <div className="space-y-4">
        {/* Adherence */}
        {fb.adherence?.planned_per_week && (
          <div data-testid="feedback-adherence" className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-yellow-50 flex items-center justify-center shrink-0 mt-0.5">
              <CalendarCheck size={15} className="text-yellow-700" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-bold text-gray-900">
                Edzések: {fb.adherence.last_week_sessions}/{fb.adherence.planned_per_week} a legutóbbi héten
              </div>
              <p className="text-xs text-gray-500 mt-0.5">{fb.adherence.message}</p>
            </div>
          </div>
        )}

        {/* Strength per exercise */}
        {strengthWithData.slice(0, 6).map((s) => {
          const last = s.series[s.series.length - 1];
          return (
            <div key={s.exercise} data-testid={`feedback-strength-${s.exercise}`} className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center shrink-0 mt-0.5">
                <Dumbbell size={15} className="text-gray-600" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-bold text-gray-900">{s.exercise}</span>
                  <VerdictChip verdict={s.verdict} testId={`verdict-strength-${s.exercise}`} />
                  {s.change_pct != null && s.verdict !== 'gyűjtés' && (
                    <span className="text-xs text-gray-500">{s.change_pct > 0 ? '+' : ''}{s.change_pct}%</span>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-0.5">
                  {showE1rm
                    ? `Heti legjobb becsült max: ${last.e1rm} kg (${last.kg} kg × ${last.reps} ism., RPE ${10 - last.rir})`
                    : `Legjobb szett a héten: ${last.kg} kg × ${last.reps} ism. (RPE ${10 - last.rir})`}
                  {' — '}{s.message}
                </p>
              </div>
            </div>
          );
        })}

        {/* Weight + waist */}
        {(fb.weight?.series?.length > 0 || fb.waist?.series?.length > 0) && (
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center shrink-0 mt-0.5">
              <Ruler size={15} className="text-gray-600" />
            </div>
            <div className="min-w-0 space-y-1.5">
              {fb.weight?.series?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-bold text-gray-900">Testsúly (heti átlag)</span>
                    <VerdictChip verdict={fb.weight.verdict} testId="verdict-weight" />
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">{fb.weight.message}</p>
                </div>
              )}
              {fb.waist?.series?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-bold text-gray-900">Derékméret</span>
                    <VerdictChip verdict={fb.waist.verdict} testId="verdict-waist" />
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">{fb.waist.message}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <p className="text-[11px] text-gray-400 mt-5 border-t border-gray-100 pt-3">
        A testsúlyt elég heti 2–3× mérni, mindig azonos körülmények között (reggel, éhgyomorra) — a heti átlagot nézzük,
        így a napi ingadozás nem számít. A körméret kéthetente-havonta elég.
      </p>
    </div>
  );
};

const CoachMessages = ({ messages, onSent }) => {
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const thread = (messages || []).filter((m) => !m.log_id);

  const send = async () => {
    if (reply.trim().length < 2) return;
    setSending(true);
    try {
      await portalCoachMessageSend(reply.trim());
      toast.success('Üzenet elküldve Gábornak.');
      setReply('');
      onSent();
    } catch (_) { toast.error('A küldés nem sikerült.'); }
    finally { setSending(false); }
  };

  return (
    <div data-testid="portal-coach-messages" className="rounded-2xl border border-gray-200 bg-white p-5 md:p-7">
      <h3 className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-4 flex items-center gap-2">
        <MessageSquareText size={14} className="text-yellow-700" /> Üzenetek Gábortól — heti check-in
      </h3>
      <div className="space-y-2 max-h-72 overflow-y-auto mb-4">
        {thread.length === 0 && (
          <p className="text-sm text-gray-500">Itt jelennek meg Gábor visszajelzései a haladásodról — és itt tudsz válaszolni is neki.</p>
        )}
        {thread.map((m) => (
          <div key={m.id} data-testid={`portal-msg-${m.id}`}
            className={`rounded-xl px-4 py-2.5 text-sm max-w-[85%] ${m.sender === 'coach'
              ? 'bg-gray-900 text-gray-100'
              : 'bg-yellow-50 border border-yellow-200 text-gray-800 ml-auto'}`}>
            <div className={`text-[10px] uppercase tracking-widest mb-0.5 ${m.sender === 'coach' ? 'text-yellow-400' : 'text-yellow-700'}`}>
              {m.sender === 'coach' ? 'Gábor' : 'Te'} · {fmtFull(m.created_at)}
            </div>
            <div className="whitespace-pre-wrap">{m.message}</div>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <Textarea data-testid="portal-msg-input" value={reply} onChange={(e) => setReply(e.target.value)} rows={2}
          placeholder="Írj Gábornak… (pl. hogyan ment a hét, mi volt nehéz)" className="text-sm" />
        <Button data-testid="portal-msg-send" onClick={send} disabled={sending || reply.trim().length < 2}
          className="bg-gray-900 hover:bg-yellow-600 text-white font-bold shrink-0 self-end">
          {sending ? <Loader2 className="animate-spin" size={14} /> : <><Send size={14} className="mr-1.5" /> Küldés</>}
        </Button>
      </div>
    </div>
  );
};

export const TrainingSection = () => {
  const [data, setData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [t, m] = await Promise.all([
        portalTraining(),
        portalCoachMessages().catch(() => ({ messages: [] })),
      ]);
      setData(t);
      setMessages(m.messages || []);
    }
    catch (_) { /* section is optional; stay silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const chartData = useMemo(() => {
    if (!data) return [];
    const rows = [];
    if (data.baseline && (data.baseline.weight_kg || data.baseline.waist_cm)) {
      rows.push({
        date: `${fmtD(data.baseline.date)} (kezdés)`,
        Testsúly: data.baseline.weight_kg, Derék: data.baseline.waist_cm,
        Nyak: data.baseline.neck_cm, Csípő: data.baseline.hip_cm,
        Váll: data.baseline.shoulder_cm,
      });
    }
    (data.measurements || []).forEach((m) => rows.push({
      date: fmtD(m.created_at),
      Testsúly: m.weight_kg, Derék: m.waist_cm, Nyak: m.neck_cm,
      Csípő: m.hip_cm, Váll: m.shoulder_cm,
    }));
    return rows;
  }, [data]);

  const gender = data?.baseline?.gender;

  if (loading) return null;
  const plan = data?.plans?.[0];
  const base = data?.baseline;
  const latest = data?.measurements?.length ? data.measurements[data.measurements.length - 1] : null;
  const diff = latest && base && latest.weight_kg && base.weight_kg
    ? (latest.weight_kg - base.weight_kg) : null;

  const fmtSets = (l) => {
    if (l.sets?.length) {
      return l.sets.map((s) => `${s.kg}kg×${s.reps} (RPE ${10 - s.rir})`).join(' · ');
    }
    const parts = [];
    if (l.weight_kg != null) parts.push(`${l.weight_kg} kg`);
    if (l.reps != null) parts.push(`× ${l.reps} ism.`);
    if (l.sets_done != null) parts.push(`× ${l.sets_done} sor.`);
    return parts.join(' ');
  };

  return (
    <div className="mt-8 space-y-8" data-testid="portal-training-section">
      {/* Training plan */}
      {plan && (
        <div data-testid="portal-training-plan" className="rounded-2xl border border-gray-200 bg-white p-5 md:p-7">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <h3 className="text-xs font-bold uppercase tracking-widest text-gray-500 flex items-center gap-2">
              <Dumbbell size={14} className="text-yellow-700" /> Edzéstervem
            </h3>
            <Link to={`/fiokom/edzesterv/${plan.id}`} data-testid="portal-plan-print-link">
              <Button variant="outline" size="sm" className="border-gray-300 text-gray-700 hover:bg-gray-100">
                <Printer size={14} className="mr-1.5" /> Nyomtatás / PDF
              </Button>
            </Link>
          </div>
          <TrainingPlanView plan={plan} workoutLogs={data?.workout_logs || []} />
        </div>
      )}

      {/* Progress feedback */}
      {plan && data?.feedback && <ProgressFeedback feedback={data.feedback} />}

      {/* Coach messages (check-in) */}
      {plan && <CoachMessages messages={messages} onSent={load} />}

      {/* Workout log (online clients) */}
      {plan && (
        <div data-testid="portal-workout-log" className="rounded-2xl border border-gray-200 bg-white p-5 md:p-7">
          <h3 className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-4 flex items-center gap-2">
            <TrendingUp size={14} className="text-yellow-700" /> Edzésnapló — mennyi ment ki?
          </h3>
          <WorkoutLogForm plan={plan} workoutLogs={data?.workout_logs} onSaved={load} />
          <OneRmTrendChart workoutLogs={data?.workout_logs} />
          {data?.workout_logs?.length > 0 && (() => {
            // Compute per-exercise running best 1RM in CHRONOLOGICAL order.
            // For each log, `bestBeforeById[log.id]` = the highest estimated
            // 1RM of any EARLIER log of the same exercise. Comparing the
            // current lift against that best is what enables the PR badge —
            // if we included the current log itself, the strict-greater
            // check `currentEst > best` would be false by construction.
            const chronological = [...data.workout_logs].sort((a, b) => {
              const ta = new Date(a.created_at || 0).getTime();
              const tb = new Date(b.created_at || 0).getTime();
              return ta - tb;
            });
            const runningBest = {};       // exercise → best 1RM SO FAR
            const bestBeforeById = {};    // log.id → best 1RM BEFORE this log
            const prevLogById = {};       // log.id → most recent PRIOR log of the same exercise (for the "előző edzés" chip)
            const lastByEx = {};          // exercise → most recent log seen so far
            const overallBest = {};       // exercise → best 1RM across all logs (for %-vs-PR)
            chronological.forEach((log) => {
              const key = (log.exercise_name || '').trim().toLowerCase();
              if (!key) return;
              bestBeforeById[log.id] = runningBest[key] || null;
              prevLogById[log.id] = lastByEx[key] || null;
              lastByEx[key] = log;
              const est = oneRmEpley(log.weight_kg, log.reps);
              if (est != null) {
                if (!runningBest[key] || est > runningBest[key]) runningBest[key] = est;
                if (!overallBest[key] || est > overallBest[key]) overallBest[key] = est;
              }
            });
            // Same humanAgo helper used elsewhere — inline to avoid an import cycle
            const humanAgo = (iso) => {
              if (!iso) return '';
              const days = Math.floor((Date.now() - new Date(iso).getTime()) / (24 * 3600 * 1000));
              if (days < 1) return 'ma';
              if (days === 1) return 'tegnap';
              if (days < 7) return `${days} napja`;
              if (days < 30) { const w = Math.floor(days / 7); return w === 1 ? '1 hete' : `${w} hete`; }
              if (days < 365) { const m = Math.floor(days / 30); return m === 1 ? '1 hónapja' : `${m} hónapja`; }
              const y = Math.floor(days / 365); return y === 1 ? '1 éve' : `${y} éve`;
            };
            return (
            <div className="mt-5 border-t border-gray-100 pt-4">
              <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-2">Legutóbbi bejegyzések</div>
              <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
                {data.workout_logs.slice(0, 30).map((l) => {
                  const comment = messages.find((m) => m.log_id === l.id && m.sender === 'coach');
                  const key = (l.exercise_name || '').trim().toLowerCase();
                  const max1 = overallBest[key];
                  const currentEst = oneRmEpley(l.weight_kg, l.reps);
                  const pct = (max1 && l.weight_kg)
                    ? (parseFloat(l.weight_kg) / max1) * 100
                    : null;
                  const zone = pct != null ? percentZone(pct) : null;
                  // PR = current log's estimated 1RM beats every EARLIER log
                  // of the same exercise (strict > against the running best
                  // before this row was recorded).
                  const bestBefore = bestBeforeById[l.id];
                  const isPr = !!(currentEst && bestBefore && currentEst > bestBefore);
                  const prev = prevLogById[l.id];
                  const prevKg = prev ? parseFloat(prev.weight_kg) : null;
                  const curKg  = parseFloat(l.weight_kg);
                  const deltaKg = (prevKg && curKg) ? Math.round((curKg - prevKg) * 10) / 10 : null;
                  const deltaPct = (prevKg && curKg) ? Math.round(((curKg / prevKg) - 1) * 100) : null;
                  return (
                  <div
                    key={l.id}
                    data-testid={`workout-log-row-${l.id}`}
                    className="rounded-lg border border-gray-100 bg-gray-50/60 px-3 py-2"
                  >
                    <div className="flex flex-wrap items-center justify-between text-sm gap-2">
                      <span className="font-semibold text-gray-800">{l.exercise_name}</span>
                      <span className="text-gray-600">
                        {fmtSets(l)}
                        <span className="text-gray-400 ml-2 text-xs">{fmtFull(l.created_at)}{l.logged_by === 'coach' ? ' · Gábor írta be' : ''}</span>
                      </span>
                    </div>

                    {/* "Előző edzés" comparison — shows the previous log of the
                        same exercise so the athlete sees BOTH the current and
                        one-before results in a single row without scrolling. */}
                    {prev && (
                      <div
                        data-testid={`workout-log-prev-${l.id}`}
                        className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-gray-500"
                      >
                        <span className="uppercase tracking-widest text-[9px] text-gray-400 font-bold">Előző</span>
                        <span className="text-gray-700">
                          <strong>{prev.weight_kg} kg × {prev.reps}</strong>
                          <span className="text-gray-400"> · {humanAgo(prev.created_at)}</span>
                        </span>
                        {deltaKg != null && deltaKg !== 0 && (
                          <span className={`font-bold ${deltaKg > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                            {deltaKg > 0 ? '▲' : '▼'} {Math.abs(deltaKg)} kg{deltaPct != null && ` (${deltaPct > 0 ? '+' : ''}${deltaPct}%)`}
                          </span>
                        )}
                        {deltaKg === 0 && (
                          <span className="text-gray-400 font-semibold">= azonos terhelés</span>
                        )}
                      </div>
                    )}

                    {/* 1RM breakdown: shown only when we have both a current
                        weight × reps AND a historical PR to score against.
                        Otherwise the row stays clean like before. */}
                    {pct != null && zone && (
                      <div
                        data-testid={`onerm-panel-${l.id}`}
                        className="mt-2 rounded-md border border-gray-200 bg-white px-3 py-2"
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
                          <div className="text-gray-700">
                            <span className="font-bold text-gray-900">{Math.round(pct)}%</span>{' '}
                            <span className="text-gray-500">a becsült 1RM-ből</span>
                            <span className="ml-2 text-gray-400">
                              · 1RM ≈ <span className="text-gray-700 font-semibold">{Math.round(max1)} kg</span>
                            </span>
                            {isPr && (
                              <span className="ml-2 text-emerald-700 font-bold" data-testid={`onerm-pr-${l.id}`}>
                                🎉 új PR!
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider text-white ${zone.color}`}>
                              {zone.label}
                            </span>
                            <span className="text-[10px] text-gray-500 font-semibold">RPE {zone.rpe}</span>
                          </div>
                        </div>
                        <OneRmBar percent={pct} />
                        {!zone.ok && (
                          <div className="mt-1.5 text-[11px] text-gray-500">
                            Az izomépítéshez érdemes legalább <span className="font-bold text-emerald-700">70%</span>-on dolgozni.
                          </div>
                        )}
                      </div>
                    )}

                    {comment && (
                      <div data-testid={`log-comment-${l.id}`} className="mt-2 rounded-lg bg-yellow-50 border border-yellow-200 px-3 py-1.5 text-xs text-gray-700">
                        <span className="font-bold text-yellow-800">Gábor:</span> {comment.message}
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            </div>
            );
          })()}
        </div>
      )}

      {/* Body measurements + chart */}
      <div data-testid="portal-measurements" className="rounded-2xl border border-gray-200 bg-white p-5 md:p-7">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 className="text-xs font-bold uppercase tracking-widest text-gray-500 flex items-center gap-2">
            <Ruler size={14} className="text-yellow-700" /> Haladásom — testsúly és körméretek
          </h3>
          {diff != null && (
            <span data-testid="portal-weight-diff" className={`text-sm font-bold px-3 py-1 rounded-full ${diff <= 0 ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
              {diff <= 0 ? '' : '+'}{diff.toFixed(1)} kg a kezdés óta
            </span>
          )}
        </div>
        <MeasurementForm gender={gender} onSaved={load} />
        {chartData.length >= 2 && (
          <div className="mt-6 h-64" data-testid="portal-progress-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 5, right: 10, bottom: 0, left: -15 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} domain={['auto', 'auto']} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="Testsúly" stroke="#ca8a04" strokeWidth={2} connectNulls dot={{ r: 3 }} />
                <Line type="monotone" dataKey="Derék" stroke="#0284c7" strokeWidth={2} connectNulls dot={{ r: 3 }} />
                {gender === 'male' && (
                  <Line type="monotone" dataKey="Váll" stroke="#7c3aed" strokeWidth={1.5} connectNulls dot={{ r: 2 }} />
                )}
                {gender === 'female' && (
                  <Line type="monotone" dataKey="Csípő" stroke="#e11d48" strokeWidth={1.5} connectNulls dot={{ r: 2 }} />
                )}
                {!gender && (
                  <>
                    <Line type="monotone" dataKey="Nyak" stroke="#9ca3af" strokeWidth={1.5} connectNulls dot={{ r: 2 }} />
                    <Line type="monotone" dataKey="Csípő" stroke="#e11d48" strokeWidth={1.5} connectNulls dot={{ r: 2 }} />
                  </>
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
        {base && (
          <p className="text-xs text-gray-500 mt-3">
            Kezdőértékek a kalkulátorból ({fmtFull(base.date)}): {base.weight_kg ? `${base.weight_kg} kg` : ''}
            {base.waist_cm ? ` · derék ${base.waist_cm} cm` : ''}
            {gender === 'male' && base.shoulder_cm ? ` · váll ${base.shoulder_cm} cm` : ''}
            {gender === 'female' && base.hip_cm ? ` · csípő ${base.hip_cm} cm` : ''}
            {!gender && base.neck_cm ? ` · nyak ${base.neck_cm} cm` : ''}
            {!gender && base.hip_cm ? ` · csípő ${base.hip_cm} cm` : ''}
            {base.body_fat_pct ? ` · testzsír ${base.body_fat_pct}%` : ''}
          </p>
        )}
      </div>
    </div>
  );
};

export default TrainingSection;
